"use client"

import { useEffect, useRef } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import type { BladeEntry } from "@/lib/blade-stack"
import type { AtsEntityTypeInfo } from "@/types/ats-explorer-api"

export interface RecordDeepLink {
  entity: string
  id: string
}

/** Reads `?entity=…&record=…` once the entity list is known */
export function readRecordDeepLink(params: URLSearchParams): RecordDeepLink | null {
  const entity = params.get("entity")
  const id = params.get("record")
  return entity && id ? { entity, id } : null
}

/**
 * Mirrors the explorer's selection into the URL so a refresh, a shared link or the
 * browser back button lands on the same entity and record: `?view=explorer&entity=X`
 * with `&record=ID` while a record blade is open. Only the bottom record blade is
 * mirrored; deeper blades are session-only.
 */
export function useRecordDeepLink(
  entityTypes: AtsEntityTypeInfo[],
  selectedEntity: AtsEntityTypeInfo | null,
  stack: BladeEntry[],
  restore: (entity: AtsEntityTypeInfo, recordId: string | null) => void
) {
  const router = useRouter()
  const pathname = usePathname()
  const params = useSearchParams()
  const restored = useRef(false)

  // Restore once the entity types have loaded
  useEffect(() => {
    if (restored.current || entityTypes.length === 0) return
    restored.current = true
    const entityName = params.get("entity")
    if (!entityName) return
    const entity = entityTypes.find((e) => e.name.toLowerCase() === entityName.toLowerCase())
    if (entity) restore(entity, params.get("record"))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityTypes])

  // Mirror the current state
  const recordEntry = stack.find((entry) => entry.kind === "record") as BladeEntry<{ entityType: string; id: string | null }> | undefined
  const recordId = recordEntry && recordEntry.params.entityType === selectedEntity?.name ? recordEntry.params.id : null
  useEffect(() => {
    if (!restored.current || params.get("view") !== "explorer") return
    const next = new URLSearchParams(params.toString())
    if (selectedEntity) next.set("entity", selectedEntity.name)
    else next.delete("entity")
    if (recordId) next.set("record", recordId)
    else next.delete("record")
    if (next.toString() !== params.toString()) router.replace(`${pathname}?${next.toString()}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity?.name, recordId])
}
